import React from 'react';
const { updateSyncErrors, touch } = require('redux-form/lib/actions').default;

import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { isAnEmail } from '@eventbrite/validators';
import { gettext } from '@eventbrite/i18n';

import {
    SIGN_IN_METHOD_FACEBOOK_LOGIN,
    SIGN_IN_METHOD_APPLE_LOGIN,
    SIGN_IN_METHOD_GOOGLE_LOGIN,
    SIGN_IN_METHOD_PASSWORD,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
} from '@eventbrite/social-login-facebook';

import {
    setAccountExistent,
    updateFormType,
    showAuthNotification,
    clearAuthNotification,
    setShowModal,
    redirectToSignUp,
    redirectToLogIn,
    updateCheckoutFormType,
    existingSoftAccountCheck,
} from './auth';

import { notifyParent } from '../utils';

import { socialLogInSoftUser, storeSocialDataOnRedis } from './social';

import {
    SOCIAL_LOOK_UP_PATH,
    LOOKUP_SOCIAL_USER_EXISTENT,
    LOOKUP_SOCIAL_USER_UNEXISTENT,
    LOG_IN_FORM,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_FACEBOOK_SOFT_ACCOUNT,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_GOOGLE_SOFT_ACCOUNT,
    BASE_URL,
    LOG_IN_PATH,
    SOCIAL_LOG_IN_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
} from '../constants';

import { LOOKUP_ERROR_SPEC } from '../api/index';

import {
    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_GOOGLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_AND_FB_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_APPLE_AND_FB_ERROR_MESSAGE,
    NOT_EMAIL_ASSOCIATED,
    SIGNUP_EMAIL_EXISTS,
    CREATE_ACCOUNT,
    SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE,
    EMAIL_UNEXISTENT_ERROR_MESSAGE,
    EMAIL_NONEXISTENT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
    EMAIL_SOFT_ACCOUNT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
    ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
} from '../constants/messages';

import {
    GA_FACEBOOK,
    GA_APPLE,
    GA_EVENTBRITE,
    GA_GOOGLE,
    HEAP_EVENT_NAME_WEB_SIGNIN_ENTER_EMAIL,
} from '../constants/analytics';

import {
    trackEnterEmailSoftAccount,
    trackEnterEmailExistingAccount,
    trackEnterEmailNewAccount,
    trackContinueCreateAccountClick,
    trackContinueCreateAccountExists,
    trackContinueCreateAccountNew,
    trackAccountAlreadyConnectedWithSocials,
    trackSIWA_EmailNotFound,
    trackCheckoutEnterEmailExistingAccount,
    trackCheckoutEnterEmailSoftAccount,
    trackCheckoutEnterEmailNewAccount,
} from './gaTracking';

import { isLoginProviderEnabled, hasSocialAccount } from '../utils';

import { lookupUser as lookupUserApi } from '../api';

import { track } from '@eventbrite/datalayer-library';

/**  ACTION CREATORS  **/
export const SET_SIGNUP_SECOND_STEP = 'SET_SIGNUP_SECOND_STEP';
export const setSignupSecondStep = (payload) => ({
    type: SET_SIGNUP_SECOND_STEP,
    payload,
});
export const SET_SOCIAL_CONNECTED = 'setSocialConnected';
export const setSocialConnected = (socialConnected) => ({
    type: SET_SOCIAL_CONNECTED,
    payload: socialConnected,
});

export const SET_LOOKUP_LOADING = 'setLookupLoading';
export const setLookupLoading = (payload) => ({
    type: SET_LOOKUP_LOADING,
    payload,
});

export const SET_ENABLE_AUTH0_LOGIN_FOR_USER = 'setEnableAuth0LoginForUser';
export const setEnableAuth0LoginForUser = (enableAuth0LoginForUser) => {
    return {
        type: SET_ENABLE_AUTH0_LOGIN_FOR_USER,
        payload: !!enableAuth0LoginForUser,
    };
};
export const SET_AUTHENTICATION_REDIRECT_URL = 'setAuthenticationRedirectUrl';
export const handleAuthenticationRedirect = (auth0RedirectUrl) => {
    return {
        type: SET_AUTHENTICATION_REDIRECT_URL,
        payload: auth0RedirectUrl,
    };
};
/** ACTIONS **/
/**
 * Main logic for the lookup action
 * @param {Object} arguments - arguments passed to the function.
 * @param {string} arguments.email - The email of the user.
 * @param {string} arguments.sub - Special key for apple, Undefined if not coming from apple.
 * @param {boolean} arguments.isSignup - Signals if coming from the 'creat account' flow.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 */

export const lookupUser =
    ({ email, sub, isSignup = false }) =>
    (dispatch, getState) => {
        const {
            social: { socialProvider },
        } = getState();

        if (email && isAnEmail(email)) {
            if (!socialProvider && !isSignup) {
                // Only save execution in EB Login
                dispatch(setLookupLoading(true));
            }
            return lookupUserApi(email, socialProvider, sub)
                .then((response) => {
                    if (socialProvider) {
                        return handleLookupUserResponseSocial({
                            response,
                            dispatch,
                            getState,
                            email,
                        }).catch((error) => Promise.reject(error));
                    } else {
                        if (isSignup) {
                            trackContinueCreateAccountClick({
                                state: getState(),
                                sourceProvider: GA_EVENTBRITE,
                            });
                            // For signup, different response
                            dispatch(handleSignupUserResponse(response, email));
                        } else {
                            dispatch(handleLookupUserResponse(response, email));
                            dispatch(setLookupLoading(false));
                        }
                    }
                })
                .catch((error) => {
                    dispatch(setLookupLoading(false));
                    throw error;
                });
        } else {
            dispatch(clearAuthNotification());

            dispatch(setAccountExistent(''));

            if (socialProvider) {
                return Promise.reject();
            }
        }
    };

export const lookupUserForCheckout =
    ({ email }) =>
    (dispatch, getState) =>
        lookupUserApi(email).then((response) =>
            handleLookupUserResponseForCheckout(
                response,
                dispatch,
                getState,
                email,
            ),
        );

/**
 *
 * @param {*} param0
 * @param {*} param1
 * @returns
 */
/* istanbul ignore next */
export const lookupUserForLinking =
    ({ email }, { accessToken }) =>
    (dispatch, getState) =>
        lookupUserApi(email).then((response) =>
            handleLookupUserResponseForLinking(
                response,
                dispatch,
                getState,
                email,
                accessToken,
            ),
        );

/** RESPONSE HANDLERS **/
/**
 * Handles the response from the api if the user is trying to Login without using the Social Buttons
 * @param {Object} response - response from the 'lookupUserApi'.
 * @param {string} email - The email of the user.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 */
export const handleLookupUserResponse =
    (response, email) => (dispatch, getState) => {
        const state = getState();
        const {
            social: { socialSSOSettings },
        } = state;
        const {
            user_id: userId,
            sign_in_methods: signInMethods,
            enable_auth0_login_for_user: enableAuth0LoginForUser,
            auth0_redirect_url: auth0RedirectUrl,
        } = response;

        /*
        The subsequent routes (login/signup/etc) expect email to be in state, and the
        account status to be checked, so need to update store before dealing with the response
        logic. When setting account status as checked, the email reducer is
        also listening and will update.
        */
        dispatch(setAccountExistent(email));
        dispatch(clearAuthNotification());
        dispatch(setSocialConnected(null));
        dispatch(setSignupSecondStep(false));
        dispatch(setEnableAuth0LoginForUser(enableAuth0LoginForUser));
        dispatch(handleAuthenticationRedirect(auth0RedirectUrl));
        if (userId) {
            if (includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
                trackEnterEmailExistingAccount({ state });
                track({
                    eventName: HEAP_EVENT_NAME_WEB_SIGNIN_ENTER_EMAIL,
                    eventData: { accountStatus: 'existing account' },
                });
            } else if (isEmpty(signInMethods)) {
                trackEnterEmailSoftAccount({ state });
                // dispatch action to open popup soft account
                dispatch(setShowModal(true));
            } else {
                checkSocialAccountStatus({
                    socialSSOSettings,
                    signInMethods,
                    state,
                })(dispatch);
            }
        } else {
            trackEnterEmailNewAccount({ state });
            track({
                eventName: HEAP_EVENT_NAME_WEB_SIGNIN_ENTER_EMAIL,
                eventData: { accountStatus: 'new account' },
            });
            dispatch(
                showAuthNotification({
                    message: NOT_EMAIL_ASSOCIATED,
                    iconType: null,
                    onClick: redirectToSignUp,
                    buttonCTA: CREATE_ACCOUNT,
                }),
            );
            dispatch(setSignupSecondStep(true));
            dispatch(
                updateSyncErrors(
                    LOG_IN_FORM,
                    LOOKUP_ERROR_SPEC['USER_NOT_EXISTS'],
                ),
            );
            dispatch(touch(LOG_IN_FORM, 'email'));
        }
    };

/** RESPONSE HANDLERS FOR SIGNUP **/
/**
 * Handles the response from the api if the user is trying to Signup without using the Social Buttons
 * @param {Object} response - response from the 'lookupUserApi'.
 * @param {string} email - The email of the user.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 */
export const handleSignupUserResponse =
    (response, email) => (dispatch, getState) => {
        const state = getState();
        const {
            social: { socialSSOSettings },
        } = state;
        const { user_id: userId, sign_in_methods: signInMethods } = response;

        dispatch(setAccountExistent(email));
        dispatch(clearAuthNotification());
        if (userId) {
            if (isEmpty(signInMethods)) {
                trackEnterEmailSoftAccount({ state });
                // dispatch action to open popup soft account
                dispatch(setShowModal(true));
            } else if (includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
                trackContinueCreateAccountExists({
                    state,
                    sourceProvider: GA_EVENTBRITE,
                });
                dispatch(
                    showAuthNotification({
                        message: SIGNUP_EMAIL_EXISTS,
                        iconType: null,
                        onClick: redirectToLogIn,
                        buttonCTA: gettext('Log in'),
                    }),
                );
            } else {
                checkSocialAccountStatus({
                    socialSSOSettings,
                    signInMethods,
                    state,
                })(dispatch);
            }
        } else {
            trackContinueCreateAccountNew({
                state,
                sourceProvider: GA_EVENTBRITE,
            });
            dispatch(setSignupSecondStep(true));
        }
    };

/**
 * Handles the response from the api if the user is trying to Login or Signup using the Social Buttons
 * @param {Object} response - response from the 'lookupUserApi'.
 * @param {function} dispatch - Function to be hable to dispatch actions.
 * @param {function} getState - Function to be able to get the current state.
 * @param {string} email - The email of the user.
 */
export const handleLookupUserResponseSocial = ({
    response,
    dispatch,
    getState,
    email,
}) => {
    const state = getState();
    const {
        social: {
            socialSSOSettings,
            socialProvider: sourceProvider = GA_FACEBOOK,
        },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;
    const socialProviderChecker = socialProviderCheckers[sourceProvider];

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));
    if (userId) {
        // If user has account linked to this social provider we return and continue with the login
        if (
            socialProviderChecker &&
            socialProviderChecker({ socialSSOSettings, signInMethods })
        ) {
            // We reject the function so it enters the catch on appleLogin.
            // Probably it is better to provide some kind of error to ensure a proper treatment
            return Promise.reject({
                message: LOOKUP_SOCIAL_USER_EXISTENT,
            });
        }

        if (includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
            trackEnterEmailExistingAccount({ state });

            // Returning this will redirect us to LINK_ACCOUNTS_LOG_IN_PATH, that is the look familiar
            // page with the new design.
            return Promise.reject({
                message: LOOKUP_SOCIAL_USER_EXISTENT,
            });
        } else if (hasFbAccount({ socialSSOSettings, signInMethods })) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                sourceProvider,
                connectedSocials: [GA_FACEBOOK],
            });

            return Promise.reject({
                message: LOOKUP_SOCIAL_USER_EXISTENT_IN_FACEBOOK_SOFT_ACCOUNT,
            });
        } else if (hasGoogleAccount({ socialSSOSettings, signInMethods })) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                sourceProvider,
                connectedSocials: [GA_GOOGLE],
            });

            return Promise.reject({
                message: LOOKUP_SOCIAL_USER_EXISTENT_IN_GOOGLE_SOFT_ACCOUNT,
            });
        } else if (hasAppleAccount({ socialSSOSettings, signInMethods })) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                sourceProvider,
                connectedSocials: [GA_APPLE],
            });

            return Promise.resolve();
        } else {
            //User has a Soft account
            // We reject the function so it enters the catch on appleLogin.
            // Probably it is better to provide some kind of error to ensure a proper treatment
            return Promise.reject({
                message: LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
            });
        }
    } else {
        dispatch(updateFormType(SOCIAL_LOOK_UP_PATH));

        // The account is not existent so we ask the user what should he do.
        // Rejecting the promise will allow us to trigger the socialLoginAction
        // and store whatever we need in redis.
        return Promise.reject({ message: LOOKUP_SOCIAL_USER_UNEXISTENT });
    }
};

/** UTILS **/

export const checkSocialAccountStatus =
    ({ socialSSOSettings, signInMethods, state }) =>
    (dispatch) => {
        if (
            // Linked with Facebook and Apple And Google
            hasSocialAccount({
                socialSSOSettings,
                signInMethods,
                socialToCompare: [GA_FACEBOOK, GA_APPLE, GA_GOOGLE],
                useLookupSigninMethodSyntax: true,
            })
        ) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                connectedSocials: [GA_FACEBOOK, GA_APPLE, GA_GOOGLE],
            });
            dispatch(
                showAuthNotification({
                    message:
                        ACCOUNT_CONNECTED_TO_GOOGLE_APPLE_AND_FB_ERROR_MESSAGE,
                }),
            );
            dispatch(setSocialConnected([GA_FACEBOOK, GA_APPLE, GA_GOOGLE]));
        } else if (
            // Linked with Facebook and Google
            hasSocialAccount({
                socialSSOSettings,
                signInMethods,
                socialToCompare: [GA_FACEBOOK, GA_GOOGLE],
                useLookupSigninMethodSyntax: true,
            })
        ) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                connectedSocials: [GA_FACEBOOK, GA_GOOGLE],
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_GOOGLE_AND_FB_ERROR_MESSAGE,
                }),
            );
            dispatch(setSocialConnected([GA_FACEBOOK, GA_GOOGLE]));
        } else if (
            // Linked with Google and Apple
            hasSocialAccount({
                socialSSOSettings,
                signInMethods,
                socialToCompare: [GA_APPLE, GA_GOOGLE],
                useLookupSigninMethodSyntax: true,
            })
        ) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                connectedSocials: [GA_APPLE, GA_GOOGLE],
            });
            dispatch(
                showAuthNotification({
                    message:
                        ACCOUNT_CONNECTED_TO_APPLE_AND_GOOGLE_ERROR_MESSAGE,
                }),
            );
            dispatch(setSocialConnected([GA_GOOGLE, GA_APPLE]));
        } else if (
            // Linked with Facebook and Apple
            hasSocialAccount({
                socialSSOSettings,
                signInMethods,
                socialToCompare: [GA_FACEBOOK, GA_APPLE],
                useLookupSigninMethodSyntax: true,
            })
        ) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                connectedSocials: [GA_FACEBOOK, GA_APPLE],
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
                }),
            );
            dispatch(setSocialConnected([GA_FACEBOOK, GA_APPLE]));
        } else if (
            // Linked with Facebook
            hasSocialAccount({
                socialSSOSettings,
                signInMethods,
                socialToCompare: [GA_FACEBOOK],
                useLookupSigninMethodSyntax: true,
            })
        ) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                connectedSocials: [GA_FACEBOOK],
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
                }),
            );
            dispatch(setSocialConnected([GA_FACEBOOK]));
        } else if (
            // Linked with Apple
            hasSocialAccount({
                socialSSOSettings,
                signInMethods,
                socialToCompare: [GA_APPLE],
                useLookupSigninMethodSyntax: true,
            })
        ) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                connectedSocials: [GA_APPLE],
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
                }),
            );
            dispatch(setSocialConnected([GA_APPLE]));
        } else if (
            // Linked with Google
            hasSocialAccount({
                socialSSOSettings,
                signInMethods,
                socialToCompare: [GA_GOOGLE],
                useLookupSigninMethodSyntax: true,
            })
        ) {
            trackAccountAlreadyConnectedWithSocials({
                state,
                connectedSocials: [GA_GOOGLE],
            });
            dispatch(
                showAuthNotification({
                    message: ACCOUNT_CONNECTED_TO_GOOGLE_ERROR_MESSAGE,
                }),
            );
            dispatch(setSocialConnected([GA_GOOGLE]));
        }
    };
export const handleLookupUserResponseForCheckout = (
    response,
    dispatch,
    getState,
    email,
) => {
    const state = getState();
    const {
        social: { socialSSOSettings },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (login/signup/etc) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        trackCheckoutEnterEmailExistingAccount({ state });
        dispatch(updateCheckoutFormType(LOG_IN_PATH));
    } else if (hasFbAccount({ socialSSOSettings, userId, signInMethods })) {
        dispatch(updateCheckoutFormType(SOCIAL_LOG_IN_PATH));
    } else if (userId) {
        trackCheckoutEnterEmailSoftAccount({ state });
        dispatch(
            showAuthNotification({
                message: EMAIL_SOFT_ACCOUNT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
            }),
        );
        dispatch(existingSoftAccountCheck({ userHasSoftAccount: true }));
        notifyParent('checkoutLoginSoftAccount');
    } else {
        trackCheckoutEnterEmailNewAccount({ state });
        dispatch(
            showAuthNotification({
                message: EMAIL_NONEXISTENT_CONTINUE_AS_GUEST_ERROR_MESSAGE,
            }),
        );
    }
};
/**
 *
 * @param {*} response
 * @param {*} dispatch
 * @param {*} getState
 * @param {*} email
 * @param {*} accessToken
 * @param {*} sourceProvider
 */
/* istanbul ignore next */
export const handleLookupUserResponseForLinking = (
    response,
    dispatch,
    getState,
    email,
    accessToken,
) => {
    const state = getState();
    const {
        routing: {
            locationBeforeTransitions: { pathname },
        },
    } = state;
    const { user_id: userId, sign_in_methods: signInMethods } = response;

    /*
    The subsequent routes (socialLogin) expect email to be in state, and the
    account status to be checked, so need to update store before dealing with the response
    logic. When setting account status as checked, the email reducer is
    also listening and will update.
    */
    dispatch(setAccountExistent(email));

    if (
        isUndefined(userId) &&
        pathname === `${BASE_URL}/${SOCIAL_LOOK_UP_PATH}`
    ) {
        trackSIWA_EmailNotFound(state);
        dispatch(
            showAuthNotification({
                message: EMAIL_UNEXISTENT_ERROR_MESSAGE,
            }),
        );
    } else if (
        !!userId &&
        includes(signInMethods, SIGN_IN_METHOD_APPLE_LOGIN)
    ) {
        // for now, we are asking for EB account linking only in the SignIn with Apple flow,
        // in the future, when we ask for linking for other providers we should redefine this condition
        dispatch(
            showAuthNotification({
                message: SOCIAL_ACCOUNT_ALREADY_LINKED_ERROR_MESSAGE,
            }),
        );
    } else if (!!userId && includes(signInMethods, SIGN_IN_METHOD_PASSWORD)) {
        dispatch(updateFormType(SOCIAL_LOG_IN_PATH));

        /**
         * Storing the new entered email and the access_token from siwa
         * on redis for the forgotPassword flow.
         */
        dispatch(storeSocialDataOnRedis({ email, accessToken }));
    } else if (
        !!userId &&
        isEqual(signInMethods, [SIGN_IN_METHOD_FACEBOOK_LOGIN])
    ) {
        // if only has facebook as login method, we cannot link their account asking for email+password
        dispatch(
            showAuthNotification({
                message: ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
            }),
        );
    } else if (!!userId && isEmpty(signInMethods)) {
        dispatch(socialLogInSoftUser());
        dispatch(updateFormType(SET_LINKED_SOFT_ACCOUNTS_PATH));
    }
};
/**
 * Checks if the email of the user is assosiated with a Facebook AND an Apple account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasFbAndAppleAccountOnly = ({
    socialSSOSettings,

    signInMethods,
}) =>
    hasAppleAccount({ socialSSOSettings, signInMethods }) &&
    hasFbAccount({ socialSSOSettings, signInMethods }) &&
    !hasGoogleAccount({ socialSSOSettings, signInMethods });

/**
 * Checks if the email of the user is assosiated with a Facebook account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasFbAccount = ({ socialSSOSettings, signInMethods }) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    ) && includes(signInMethods, SIGN_IN_METHOD_FACEBOOK_LOGIN);

/**
 * Checks if the email of the user is assosiated with an Apple account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasAppleAccount = ({ socialSSOSettings, signInMethods }) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
    ) && includes(signInMethods, SIGN_IN_METHOD_APPLE_LOGIN);

/**
 * Checks if the email of the user is assosiated with a Google account
 * @param {Object} arguments - arguments passed to the function.
 * @param {Object} socialSSOSettings - Object with relevant data about the account of the user.
 * @param {Array} signInMethods - Array with the Login methods the user is currently able to do.
 */
export const hasGoogleAccount = ({ socialSSOSettings, signInMethods }) =>
    isLoginProviderEnabled(
        socialSSOSettings,
        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
    ) && includes(signInMethods, SIGN_IN_METHOD_GOOGLE_LOGIN);

const socialProviderCheckers = {
    ['apple']: hasAppleAccount,
    ['facebook']: hasFbAccount,
    ['google']: hasGoogleAccount,
};
